/**
 * @fileoverview Service for Facebook Pages Connect API operations.
 * @module facebook-pages-connect/services/FacebookPagesConnectService
 */

import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import type {
  FacebookPagesConnectionStatus,
  FacebookPagesDisconnectResponse,
  FacebookPagesListResponse,
  FacebookPagesOAuthUrlResponse,
  FacebookPagesPublicationStatus,
  FacebookPagesPublishRequest,
  FacebookPagesReadbackRequest,
  FacebookPagesSelectPageResponse,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }
  return 'https://api.segmently.ai';
}

export class FacebookPagesConnectService {
  private readonly apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || getApiUrl();
  }

  private async waitForCurrentUser(timeoutMs = 5000): Promise<User | null> {
    const auth = getAuth();
    if (auth.currentUser) {
      return auth.currentUser;
    }

    return new Promise((resolve) => {
      let settled = false;
      let unsubscribe: (() => void) | undefined;

      const finish = (user: User | null) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        unsubscribe?.();
        resolve(user);
      };

      const timeout = window.setTimeout(() => finish(auth.currentUser), timeoutMs);
      unsubscribe = onAuthStateChanged(auth, finish);
    });
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = await this.waitForCurrentUser();
    const token = await user?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private buildGetUrl(path: string, params: URLSearchParams): string {
    const url = new URL(`${this.apiUrl}${path}`);
    params.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
    // Avoid stale OAuth/status/page-list responses after Meta redirects.
    url.searchParams.set('_ts', String(Date.now()));
    return url.toString();
  }

  async getOAuthUrl(projectId: string, returnTo?: string): Promise<string> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (returnTo) {
      params.set('returnTo', returnTo);
    }

    const response = await fetch(this.buildGetUrl('/api/facebook-pages/oauth/url', params), {
      headers,
      cache: 'no-store',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Facebook Pages OAuth URL' }));
      throw new Error(error.error || 'Failed to get Facebook Pages OAuth URL');
    }

    const data = await response.json() as FacebookPagesOAuthUrlResponse;
    return data.url;
  }

  async getStatus(projectId: string, signal?: AbortSignal): Promise<FacebookPagesConnectionStatus> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(this.buildGetUrl('/api/facebook-pages/status', params), {
      headers,
      signal,
      cache: 'no-store',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Facebook Pages connection status' }));
      throw new Error(error.error || 'Failed to get Facebook Pages connection status');
    }

    return response.json();
  }

  async disconnect(projectId: string): Promise<FacebookPagesDisconnectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/facebook-pages/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to disconnect Facebook Pages' }));
      throw new Error(error.error || 'Failed to disconnect Facebook Pages');
    }

    return response.json();
  }

  async listPages(projectId: string): Promise<FacebookPagesListResponse> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(this.buildGetUrl('/api/facebook-pages/pages', params), {
      headers,
      cache: 'no-store',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list Facebook Pages' }));
      throw new Error(error.error || 'Failed to list Facebook Pages');
    }

    return response.json();
  }

  async selectPage(projectId: string, pageId: string): Promise<FacebookPagesSelectPageResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/facebook-pages/pages/select`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, pageId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to select Facebook Page' }));
      throw new Error(error.error || 'Failed to select Facebook Page');
    }

    return response.json();
  }

  async publish(request: FacebookPagesPublishRequest): Promise<FacebookPagesPublicationStatus> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/facebook-pages/publish`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to publish to Facebook Page' }));
      throw new Error(error.error || 'Failed to publish to Facebook Page');
    }

    return response.json();
  }

  async readback(request: FacebookPagesReadbackRequest): Promise<FacebookPagesPublicationStatus> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/facebook-pages/readback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to read Facebook Page post status' }));
      throw new Error(error.error || 'Failed to read Facebook Page post status');
    }

    return response.json();
  }
}

let singleton: FacebookPagesConnectService | null = null;

export function getFacebookPagesConnectService(): FacebookPagesConnectService {
  if (!singleton) {
    singleton = new FacebookPagesConnectService();
  }
  return singleton;
}

export function createFacebookPagesConnectService(apiUrl?: string): FacebookPagesConnectService {
  return new FacebookPagesConnectService(apiUrl);
}

export default FacebookPagesConnectService;
